import { Component, Input, Output, EventEmitter } from '@angular/core';
import { CommonModule } from '@angular/common';

@Component({
  selector: 'app-admin-modal',
  standalone: true,
  imports: [CommonModule],
  template: `
    <div class="modal-overlay" *ngIf="isOpen" (click)="onClose()">
      <div class="modal-card" (click)="$event.stopPropagation()">
        <!-- Header -->
        <div class="modal-header">
          <div class="header-content">
            <div class="icon-wrap" *ngIf="icon">
              <i [class]="icon"></i>
            </div>
            <div>
              <h3>{{ title }}</h3>
              <p *ngIf="subtitle">{{ subtitle }}</p>
            </div>
          </div>
          <button class="btn-close" (click)="onClose()">
            <i class="fa-solid fa-xmark"></i>
          </button>
        </div>

        <!-- Body -->
        <div class="modal-body custom-scroll">
          <ng-content></ng-content>
        </div>

        <!-- Footer -->
        <div class="modal-footer" *ngIf="showFooter">
          <button class="btn-cancel" (click)="onClose()">{{ secondaryText }}</button>
          <button class="btn-primary" [ngClass]="submitClass" [disabled]="isSubmitting" (click)="onSubmit()">
            <i class="fa-solid fa-spinner fa-spin" *ngIf="isSubmitting"></i>
            <span>{{ submitText }}</span>
          </button>
        </div>
      </div>
    </div>
  `,
  styles: [`
    .modal-overlay {
      position: fixed;
      inset: 0;
      background: rgba(15, 23, 42, 0.6);
      backdrop-filter: blur(4px);
      z-index: 1000;
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 20px;
      animation: fadeIn 0.2s ease-out;
    }

    .modal-card {
      background: white;
      width: 100%;
      max-width: 500px;
      border-radius: 20px;
      box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
      display: flex;
      flex-direction: column;
      max-height: 90vh;
      animation: slideUp 0.3s cubic-bezier(0.16, 1, 0.3, 1);
    }

    .modal-header {
      padding: 20px 24px;
      display: flex;
      justify-content: space-between;
      align-items: center;
      border-bottom: 1px solid #f1f5f9;
      background: #fafbfc;
      border-radius: 20px 20px 0 0;

      .header-content {
        display: flex;
        align-items: center;
        gap: 16px;
        .icon-wrap {
          width: 40px; height: 40px; border-radius: 12px; background: #eff6ff; color: #2563eb;
          display: flex; align-items: center; justify-content: center; font-size: 18px;
        }
        h3 { font-size: 18px; font-weight: 800; color: #1e293b; margin: 0; line-height: 1.2; }
        p { font-size: 12px; color: #64748b; margin: 2px 0 0; font-weight: 500; }
      }

      .btn-close {
        width: 32px; height: 32px; border-radius: 8px; border: none; background: transparent; color: #94a3b8;
        cursor: pointer; transition: all 0.2s;
        &:hover { background: #f1f5f9; color: #1e293b; }
      }
    }

    .modal-body {
      padding: 24px;
      overflow-y: auto;
      flex: 1;
    }

    .modal-footer {
      padding: 16px 24px;
      display: flex;
      justify-content: flex-end;
      gap: 12px;
      border-top: 1px solid #f1f5f9;
      background: #fafbfc;
      border-radius: 0 0 20px 20px;

      button {
        padding: 10px 20px; border-radius: 10px; font-size: 14px; font-weight: 700; cursor: pointer; transition: all 0.2s;
      }
      .btn-cancel { background: #f1f5f9; border: 1px solid #e2e8f0; color: #475569; &:hover { background: #e2e8f0; } }
      .btn-primary { 
        background: #111d4d; color: white; border: none; display: flex; align-items: center; gap: 8px;
        &:hover { background: #1a2a6c; transform: translateY(-1px); box-shadow: 0 4px 12px rgba(17, 29, 77, 0.2); }
        &:disabled { opacity: 0.6; cursor: not-allowed; transform: none; }
      }
      .danger {
        background: #dc2626; color: white;
        &:hover { background: #b91c1c; }
      }
    }

    @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
    @keyframes slideUp { from { transform: translateY(20px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
  `]
})
export class AdminModalComponent {
  @Input() isOpen: boolean = false;
  @Input() title: string = '';
  @Input() subtitle: string = '';
  @Input() icon: string = '';
  @Input() submitText: string = 'Save Changes';
  @Input() secondaryText: string = 'Cancel';
  @Input() isSubmitting: boolean = false;
  @Input() showFooter: boolean = true;
  @Input() submitClass: string = '';

  @Output() close = new EventEmitter<void>();
  @Output() submit = new EventEmitter<void>();

  onClose(): void {
    if (!this.isSubmitting) this.close.emit();
  }

  onSubmit(): void {
    this.submit.emit();
  }
}
